// MusicXML（uncompressed .xml / .musicxml）→ Sparks NMN 文本导入器
//
// 多声部：MusicXML 用「part × staff × voice」描述同时发声的线，NMN 用「碎片里的
// 多行 N:」描述同一件事。这里的做法是先把所有音符铺到「小节内时间轴」上，再按
// **时间是否重叠**把同一谱表的音分成若干层，一层输出一行 N:。
//
// 钢琴大谱表（1 个 part + 2 个 staff）是最常见的情形 → 右手一行、左手一行
// （`N["伴奏"]:`），与「自动左手伴奏」写回源码的形态一致。
//
// **按原样保留**：源谱有几个声部就输出几行，同一起点的多个音写成柱式和弦
// （`[1d,3d,5d]`），不再削薄。曾经的「最多两行 + 和弦只留根音」是为了给初学者
// 减负，代价是丢掉中间声部 —— 现在语言本身支持和弦了（见 chordVoices.ts），
// 保真比减负重要：谱面复杂说明源谱本身复杂，挑选曲目是运营的事，不是导入器的。
//
// 唯一的主动丢弃是「幽灵声部」：全曲只有一两个音的转谱残留，单独成行会让谱面
// 多一条几乎全是休止的空线。丢掉的数量记进 warning。
//
// 仍有限制：
// - 长音若不是「整数拍 + 二分档位」（例如 1.875 拍）无法用单个 token 表达，
//   会被近似并记 warning —— NMN 没有「延音合并时值」的记号，`~` 是装饰性连音线
// - 不还原反复记号（线性展开）、不还原装饰音、不还原圆滑线/力度等记号
// - 每个谱表内的真复调会拆成多行：那是同时进行的**独立旋律**（各占一行），
//   与同一起点的一簇音（柱式和弦，写进一个方括号）不是一回事
//
// 连音（`<time-modification>`）已精确还原为 `T[x,y](…)` 括号组，
// 换算式见下面 `emitTuplet` 的注释 —— 这个方向很容易记反。

const stepSemis: {[_: string]: number} = {C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11}
// 五度圈 → 大调主音半音
const fifthsToSemis: {[_: number]: number} = {
	0: 0, 1: 7, 2: 2, 3: 9, 4: 4, 5: 11, 6: 6, 7: 1,
	'-1': 5, '-2': 10, '-3': 3, '-4': 8, '-5': 1, '-6': 6, '-7': 11,
}
// ⚠️ 变化音写在**数字前**、且用 `b`/`#` 前缀：`1=bE` 而不是 `1=Eb`。
// 写成 `Eb` 会让解析器报 unknown_base，整个 `P:` 行作废 —— 降号调（降 E、降 B、
// 降 A）的谱全部导入失败，而 C/G 调的谱看起来完全正常，很容易漏掉。
const tonicNames = ['C', '#C', 'D', 'bE', 'E', 'F', '#F', 'G', 'bA', 'A', 'bB', 'B']
const degreeSemis = [0, 2, 4, 5, 7, 9, 11]

const EPS = 1e-6

export type MusicXmlImportResult = {
	title: string
	content: string
	warnings: string[]
}

/** 一个小节内的音符事件。start / quarters 单位都是四分音符。 */
type Ev = {
	start: number
	quarters: number
	midi: number | null
	tieStart: boolean
	tieStop: boolean
	lyrics: string[]
	/**
	 * MusicXML 的 `<voice>`。
	 *
	 * ⚠️ 这是**声部的正统标识**，`<staff>` 只是谱表（钢琴大谱表里左右手各一个
	 * staff，但同一个 staff 上常有两个 voice）。之前按 staff 判重，把不同 voice
	 * 同时发声的两个音当成「和弦」合并掉了，也把连音组里被别的 voice 占住的休止
	 * 判成「已占用」丢掉 —— 结果三连音组少一个音，整组退化成普通时值。
	 */
	voice: number
	/**
	 * MusicXML 的 `<time-modification>`：actual 个音符占 normal 个的位置，
	 * 即每个音的实际时值要乘 normal/actual。
	 *
	 * ⚠️ 方向别记反：NMN 的 `T[x,y]` 里 x/y 也是这个「乘数」，所以
	 * MusicXML `5:4`（actual:normal）→ NMN `T[4,5]`。照抄成 `T[5,4]` 时值差 1.56 倍。
	 */
	tuplet?: {actual: number, normal: number}
}

/** 一「层」= NMN 里的一行 N:。measures[m] 是本层在第 m 小节的事件。 */
type Layer = {
	partIdx: number
	staff: number
	voice: number
	measures: Ev[][]
	cursor: number
}

type ParsedPart = {
	/** raw[m] = 第 m 小节里 staff → 事件 */
	raw: Map<number, Ev[]>[]
	hasPitch: boolean
}

type ScoreAttrs = {
	fifths: number
	beats: number
	beatType: number
	qpm: number
}

function num(el: Element | null): number | undefined {
	if(!el || !el.textContent) return undefined
	const v = parseFloat(el.textContent)
	return isNaN(v) ? undefined : v
}

// 可表示的时值。**必须包含 0.125 / 0.0625**：钢琴谱里三十二分音符很常见，
// 只写到十六分会让 0.125 拍被吸附成 0.25（音符时值凭空翻倍，小节内部节奏全错，
// 但因为补白逻辑会把小节凑齐，外部只看小节总长根本看不出问题）。
const SNAP_STEPS = [4, 3, 2.5, 2, 1.5, 1, 0.75, 0.5, 0.25, 0.125, 0.0625]

	// 兜底提示：连音已经能用 `T[x,y]` 精确表示，走到这条说明遇到了没覆盖的时值形态。
// 目前不做多连音还原，但必须让用户看到原因，不能只剩「小节线错位」这种哑症状。
	const TUPLET_WARNING = '小节时值与拍号对不上（出现无法精确表示的时值），建议导入后手动核对'

/** 四分音符数 → 最近的「NMN 能写出来」的时值 */
function snapDuration(quarters: number) {
	return SNAP_STEPS.reduce((a, b) => Math.abs(b - quarters) < Math.abs(a - quarters) ? b : a)
}

/** 把「可表示的时值」写成 NMN 记号（不含音高）。 */
function plainToken(digitToken: string, quarters: number): string | null {
	if(quarters >= 1) {
		// 整数拍用延长线接（`-` 每个 1 拍）；以 .5 结尾的写成附点再补延长线
		let token = digitToken
		if(Math.abs(quarters % 1 - 0.5) < 1e-6) token += '.'
		else if(quarters % 1 != 0) return null
		token += ' -'.repeat(Math.round(Math.floor(quarters)) - 1)
		return token
	}
	// 减时线：0.5 拍 = 一层括号，0.25 = 两层，0.125 = 三层，0.0625 = 四层；
	// 附点则在同层基础上加 `.`（0.75 = 0.5×1.5 → `(1.)`，0.375 → `((1.))`）。
	for(let level = 1; level <= 4; level++) {
		const base = 1 / Math.pow(2, level)
		if(Math.abs(quarters - base) < 1e-9) {
			return '('.repeat(level) + digitToken + ')'.repeat(level)
		}
		if(Math.abs(quarters - base * 1.5) < 1e-9) {
			return '('.repeat(level) + digitToken + '.' + ')'.repeat(level)
		}
	}
	return null
}

// 四分音符数 → NMN 音符记号（不含音高）
function durationToken(digitToken: string, quarters: number, warnings: string[]): string | null {
	// 先看能不能直接写成档位上的时值（附点优先，谱面更自然）
	const exact = SNAP_STEPS.find(step => Math.abs(step - quarters) < 1e-6)
	if(exact !== undefined) {
		const token = plainToken(digitToken, exact)
		if(token) return token
	}

	// 不在档位上就拆成「整数拍 + 余数」。延长线每个正好 1 拍、且个数不限，所以
	// 1.25 = `((1)) -`、1.75 = `(1.) -`、7 拍 = `1 - - - - - -` 都能**精确**写出来。
	// 这比「吸附到最近档位」重要得多：长音被吸附会直接改变节奏，而且小节总长照样
	// 对得上（补白会把差值吃掉），外面完全看不出问题。
	const whole = Math.floor(quarters + 1e-6)
	const rest = quarters - whole
	if(whole <= 64) {
		if(rest < 1e-6) {
			if(whole >= 1) return digitToken + ' -'.repeat(whole - 1)
		} else {
			const restToken = plainToken(digitToken, rest)
			if(restToken) return restToken + ' -'.repeat(whole)
		}
	}

	// 实在写不出来（比如 1/24 拍）才退回吸附，并明确提示
	const snapped = snapDuration(quarters)
	const msg = `时值 ${quarters.toFixed(2)} 拍被近似为 ${snapped} 拍`
	if(!warnings.includes(msg)) warnings.push(msg)
	return plainToken(digitToken, snapped)
}

const REST_STEPS = [4, 3, 2, 1, 0.5, 0.25, 0.125, 0.0625]

/**
 * 补白休止：保证写出的休止**总和与缺口完全一致**。
 * 直接用 durationToken('0', gap) 会把缺口吸附到最近的时值，小节总长就变了 ——
 * 多声部里这会让各行的时长对不齐，小节线整体错位。
 *
 * 顺序很重要：**必须先把 1/3 的整数倍单独拎出来**。
 * 连音位置错开后留下的缺口常常正好是 1/3 拍，先贪心二分档位会拆成
 * `1/4 + 1/16 = 5/16`，剩 1/48 无论如何都表达不了（`1/3 - 1/4 - 1/16 = 1/48`），
 * 只能丢掉 —— 那一行的小节就短 1/48，多声部下小节线整体错位。
 * 实测舒曼《童年情景》第 1 首、巴赫《Ave Maria》等 11 份谱正是这么错的。
 */
function restTokens(quarters: number, warnings: string[]): string[] {
	const out: string[] = []
	if(quarters <= EPS) return out
	// 1) 整拍部分用普通休止
	const whole = Math.floor(quarters + EPS)
	let left = quarters - whole
	if(whole > 0) {
		const tok = durationToken('0', whole, warnings)
		if(tok) out.push(tok)
		else left = quarters
	}
	// 2) 余数是 1/3 的整数倍 → 用 T 组的休止精确表达（每个 0 = 1/3 拍）
	const thirds = left * 3
	const m = Math.round(thirds)
	if(left > EPS && m >= 1 && m <= 12 && Math.abs(thirds - m) < 1e-6) {
		out.push(m == 3 ? 'T(0 0 0)' : `T[${2 * m},${3 * m}](` + Array(m).fill('0').join(' ') + ')')
		left = 0
	}
	// 3) 剩下的按二分档位贪心
	let guard = 0
	while(left > EPS && guard++ < 128) {
		const step = REST_STEPS.find(s => s <= left + EPS)
		if(step === undefined) {
			warnings.push(`不足十六分音符的休止 ${left.toFixed(3)} 拍被忽略`)
			break
		}
		const tok = durationToken('0', step, warnings)
		if(tok) out.push(tok)
		left -= step
	}
	return out
}

/**
 * 歌词里不能出现 NMN 的语法字符。最典型的是双引号：`Lc: 2."Ach` 会被词法分析
 * 当成字符串开头，直接报 `unclosed_string`，整行歌词连带整篇谱都变成语法错误。
 * 这些字符都换成字形相近的全角字符 —— 用户看歌词时几乎无感，解析器则安全。
 *
 * 另外词内空格必须去掉：NMN 里空格是「音符位置分隔符」，一个词里带空格会被
 * 拆成两个位置，歌词与音符就整体错开一格。
 */
const LYRIC_ESCAPE: {[_: string]: string} = {
	'"': '\u201c', "'": '\u2019', '|': '\uff5c', '(': '\uff08', ')': '\uff09',
	'[': '\uff3b', ']': '\uff3d', ';': '\uff1b', '=': '\uff1d', '~': '\uff5e',
	'<': '\uff1c', '>': '\uff1e', '&': '\uff06', '#': '\uff03', '@': '\uff20',
}
function sanitizeLyric(text: string) {
	let out = ''
	for(const ch of text) {
		if(/\s/.test(ch)) continue
		out += LYRIC_ESCAPE[ch] ?? ch
	}
	return out
}

/** 读一个 part 的所有小节。divisions 是「每 part」独立声明的。 */
function parsePart(partEl: Element, attrs: ScoreAttrs, warnings: string[]): ParsedPart {
	const raw: Map<number, Ev[]>[] = []
	let divisions = 1
	let hasPitch = false

	for(const measure of [...partEl.children].filter(c => c.tagName == 'measure')) {
		const m = new Map<number, Ev[]>()
		let cursor = 0
		let lastStart = 0

		for(const child of [...measure.children]) {
			const tag = child.tagName
			if(tag == 'attributes') {
				const d = num(child.querySelector('divisions'))
				if(d && d > 0) divisions = d
				const f = num(child.querySelector('key > fifths'))
				if(f !== undefined) attrs.fifths = f
				const b = num(child.querySelector('time > beats'))
				const bt = num(child.querySelector('time > beat-type'))
				if(b && bt) {
					attrs.beats = b
					attrs.beatType = bt
				}
				continue
			}
			if(tag == 'direction') {
				// tempo 出现在两种形态：`<sound tempo="70"/>` 的属性里，
				// 或 `<direction-type><metronome><per-minute>` 的文本里。
				const tAttr = parseFloat(child.querySelector('sound')?.getAttribute('tempo') ?? '')
				if(!isNaN(tAttr) && tAttr > 0) {
					attrs.qpm = tAttr
				} else {
					const pm = num(child.querySelector('direction-type > metronome > per-minute'))
					if(pm && pm > 0) attrs.qpm = pm
				}
				continue
			}
			if(tag == 'backup' || tag == 'forward') {
				const d = (num(child.querySelector('duration')) ?? 0) / divisions
				cursor = Math.max(0, cursor + (tag == 'forward' ? d : -d))
				continue
			}
			if(tag != 'note') continue

			// 倚音没有时值，也不推动游标
			if(child.querySelector('grace')) continue

			const isChord = !!child.querySelector('chord')
			const quarters = (num(child.querySelector('duration')) ?? 0) / divisions
			const start = isChord ? lastStart : cursor
			if(!isChord) {
				cursor += quarters
				lastStart = start
			}

			const staff = num(child.querySelector('staff')) ?? 1
			const voice = num(child.querySelector('voice')) ?? 1
			const pitch = child.querySelector('pitch')
			let midi: number | null = null
			if(pitch) {
				hasPitch = true
				const step = pitch.querySelector('step')?.textContent?.trim() ?? 'C'
				const alter = num(pitch.querySelector('alter')) ?? 0
				const octave = num(pitch.querySelector('octave')) ?? 4
				midi = (stepSemis[step] ?? 0) + alter + (octave + 1) * 12
			}

			const ties = [...child.querySelectorAll('tie')].map(t => t.getAttribute('type'))

			// 连音标记。actual-notes/normal-notes 就是「几个音占几个的位置」，
			// 两者相等时不是连音（有些导出器会给每个音都写 1:1）。
			const tm = child.querySelector('time-modification')
			const actN = tm ? num(tm.querySelector('actual-notes')) : undefined
			const norN = tm ? num(tm.querySelector('normal-notes')) : undefined
			const tuplet = actN !== undefined && norN !== undefined
				&& actN > 1 && norN > 0 && actN != norN
				? {actual: actN, normal: norN}
				: undefined

			// 歌词：按 number 段落收集（elision/延长号合并进同一词块）
			const lyricTexts: string[] = []
			for(const lyricEl of [...child.querySelectorAll('lyric')]) {
				const numAttr = lyricEl.getAttribute('number')
				const verse = numAttr ? parseInt(numAttr.replace(/\D+/g, '')) || 1 : 1
				const text = sanitizeLyric([...lyricEl.querySelectorAll('text')].map(t => t.textContent ?? '').join(''))
				if(!text) continue
				while(lyricTexts.length < verse) lyricTexts.push('')
				lyricTexts[verse - 1] = text
			}

			let list = m.get(staff)
			if(!list) {
				list = []
				m.set(staff, list)
			}
			list.push({
				start,
				quarters,
				midi,
				tieStart: ties.includes('start'),
				tieStop: ties.includes('stop'),
				lyrics: lyricTexts,
				tuplet,
				voice,
			})
		}

		raw.push(m)
	}

	return {raw, hasPitch}
}

/**
 * 把音符按「谱表 × 声部」分成若干层，一层 = 一行 N:。
 *
 * 与旧版的区别只有一条：**不再削薄**。旧版有两条硬限制（`LAYER_CAP = 2` 与
 * 「和弦只留根音」），因为那时 NMN 一个时刻只能写一个音，同起点的一簇音要么拆成
 * 多行、要么丢掉。现在有了柱式和弦 `[135]`，一簇音可以留在同一行里，于是：
 *
 *   1. 同一起点的高叠音 → 留在**同一层**（输出时合成 `[1d,3d,5d]`）
 *   2. 起点不同、时间交叠 → 才是真复调，拆成多层，**层数由源谱决定**
 *
 * 之前丢掉中间声部的理由是「面向初学者少几行好读」，代价是伴奏声部整个消失。
 * 现在这条理由不再成立：保真优先，谱面复杂说明**源谱本身复杂**，挑曲子是运营的
 * 事，不该由导入器替用户决定弹什么。
 *
 * 层的分配用贪心：哪层已经空出来（cursor ≤ 本位置起点）就给谁。同一条
 * (part, staff, voice) 线若出现时间重叠（少数谱的 voice 标记不可靠），会再开
 * 一层而不是丢音。
 */

/**
 * 安全阀：声部数超过这个值就只保留音符最多的若干条。
 *
 * 注意这**不是**「为初学者减负」——那个理由已经被推翻了。这里唯一的用途是挡住
 * 病态文件：个别 MIDI 转谱会给每个音一个独立的 voice 号，能产生几十层。8 行已
 * 远超任何真实钢琴谱的声部数，触发了会显式告警，不静默丢。
 */
const LAYER_SAFETY_CAP = 8

function buildLayers(parts: ParsedPart[], measureCount: number, warnings: string[]): Layer[] {
	const layers: Layer[] = []
	/** 幽灵声部里被丢掉的音符总数，最后汇成一条提示 */
	let droppedNotes = 0

	// 跨谱表的「单手交替」声部：同一个 <voice> 在上下两个谱表之间来回换（中间没有
	// <backup>，光标是一条线）。钢琴谱里很常见 —— 一句旋律右手弹一半、左手接一半。
	//
	// ⚠️ 按 (staff, voice) 拆层会把这种声部切成碎片，后果是连锁的：
	//   ① 每条碎片里的连音组只剩 1~2 个音，`field = 音数 × 比例` 非整数 → 整个 T 组
	//      退化成普通时值（柴可夫斯基《一月》里 21 个小节全中）；
	//   ② 各碎片小节总长对不上 → 小节线错位。
	// 判据取「同一 voice 出现在多个谱表，且这些谱表上的音符**时间互不重叠**」。
	// 若重叠，说明确实是两个独立声部共用了同一个 voice 号（少数谱这么写），不能合并。
	const voiceStaffs = new Map<string, Set<number>>()
	/** 出现过跨谱表时间重叠的 voice —— 那种是真·两个声部共用了同一个 voice 号 */
	const voiceOverlap = new Set<string>()
	/** 每个 voice 时间上最早的那个音所在谱表，合并后统一挂靠它 */
	const voiceFirstStaff = new Map<string, {start: number, staff: number}>()
	parts.forEach((part, partIdx) => {
		for(const m of part.raw) {
			// ⚠️ 重叠判断必须**按小节**做。跨小节比会把上一小节末尾（3.0 拍）和下一
			// 小节开头（0 拍）当成重叠，于是整首曲判成「不可合并」——而实际上这种
			// 跨谱表交替声部每个小节都是干净的。
			const perKey = new Map<string, Array<{staff: number, start: number, end: number}>>()
			for(const [staff, evs] of m) {
				for(const ev of evs) {
					if(ev.midi === null) continue
					const key = `${partIdx}|${ev.voice}`
					let staffs = voiceStaffs.get(key)
					if(!staffs) voiceStaffs.set(key, staffs = new Set())
					staffs.add(staff)
					let list = perKey.get(key)
					if(!list) perKey.set(key, list = [])
					list.push({staff, start: ev.start, end: ev.start + ev.quarters})
					const first = voiceFirstStaff.get(key)
					if(!first || ev.start < first.start) voiceFirstStaff.set(key, {start: ev.start, staff})
				}
			}
			for(const [key, list] of perKey) {
				const maxEnd = new Map<number, number>()
				for(const it of list.sort((a, b) => a.start - b.start)) {
					for(const [st, end] of maxEnd) {
						if(st != it.staff && end > it.start + 1e-6) voiceOverlap.add(key)
					}
					maxEnd.set(it.staff, Math.max(maxEnd.get(it.staff) ?? 0, it.end))
				}
			}
		}
	})
	/** `${partIdx}|${voice}` → 合并后统一挂靠的谱表 */
	const mergedStaff = new Map<string, number>()
	for(const [key, staffs] of voiceStaffs) {
		if(staffs.size < 2 || voiceOverlap.has(key)) continue
		mergedStaff.set(key, voiceFirstStaff.get(key)!.staff)
	}
	/** 分层用的谱表：跨谱表声部统一归到同一个值，从而落进同一层 */
	const layerStaffOf = (partIdx: number, staff: number, voice: number) =>
		mergedStaff.get(`${partIdx}|${voice}`) ?? staff

	/** 位置键：同一起点 = 同一个「位置」，位置里的多个音就是和弦 */
	const posKeyOf = (partIdx: number, staff: number, voice: number, start: number) =>
		`${partIdx}|${staff}|${voice}|${Math.round(start * 96)}`

	type Entry = {partIdx: number, staff: number, voice: number, ev: Ev}
	/** 一个「位置」= 同一层里同一时刻的一簇音（1 个是单音，多个是和弦） */
	type Group = {
		partIdx: number
		staff: number
		voice: number
		start: number
		quarters: number
		evs: Ev[]
	}

	for(let mi = 0; mi < measureCount; mi++) {
		for(const l of layers) l.cursor = 0
		const groups = new Map<string, Group>()
		/** 本小节里「已经有音」的位置，用来判断连音休止要不要补回来 */
		const occupied = new Set<string>()
		/** 连音里的休止：等所有音分好层、确认该位置确实没音之后再补进某一层 */
		const pendingRests: Entry[] = []

		// 先把本小节的音按「位置」归组 —— 这一步必须在分配层之前做，否则和弦的
		// 第二个音会把 cursor 往前推，第一个音所在层看起来就「已被占用」，于是
		// 同一个和弦被拆成两层（这正是旧版按音高拆层的由来）。
		parts.forEach((part, partIdx) => {
			const m = part.raw[mi]
			if(!m) return
			for(const [staff, evs] of m) {
				for(const ev of evs) {
					// 谱表统一折算成「分层用的谱表」：跨谱表声部在这里被并成一个
					const ownStaff = layerStaffOf(partIdx, staff, ev.voice)
					// 休止符不参与分层：它不占声部，缺口后面统一补。
					// 若让休止也参与，整小节休止的声部会凭空多出一行全休止的 N:。
					//
					// ⚠️ 但**连音里的休止**必须留下 —— 它是节奏结构的一部分。少一个，
					// `field = 音数 × 比例` 就变成非整数，整个 T 组退化成普通时值
					// （三连音被吸附成十六分，时值错一截，且小节总长仍对得上所以不报错）。
					// 柴可夫斯基第六、舒曼等谱里的三连音大量是「休止 + 两个音」这种形态。
					if(ev.midi === null) {
						if(ev.tuplet) pendingRests.push({partIdx, staff: ownStaff, voice: ev.voice, ev})
						continue
					}
					const key = posKeyOf(partIdx, ownStaff, ev.voice, ev.start)
					occupied.add(key)
					let g = groups.get(key)
					if(!g) {
						groups.set(key, g = {
							partIdx, staff: ownStaff, voice: ev.voice,
							start: ev.start, quarters: ev.quarters, evs: [],
						})
					}
					g.evs.push(ev)
					g.quarters = Math.max(g.quarters, ev.quarters)
				}
			}
		})

		// 位置按 (part, 谱表, voice, 起点) 升序处理，层因此是稳定可复现的。
		const ordered = [...groups.values()].sort((a, b) =>
			a.partIdx - b.partIdx
			|| a.staff - b.staff
			|| a.voice - b.voice
			|| a.start - b.start)
		for(const g of ordered) {
			// 同一条线里找「已经空出来」的层；没有就再开一层（真复调，或多层同 voice）
			let target = layers.find(l => l.partIdx == g.partIdx && l.staff == g.staff
				&& l.voice == g.voice && l.cursor <= g.start + EPS)
			if(!target) {
				target = {
					partIdx: g.partIdx, staff: g.staff, voice: g.voice,
					measures: Array.from({length: measureCount}, () => []), cursor: 0,
				}
				layers.push(target)
			}
			target.measures[mi].push(...g.evs)
			target.cursor = Math.max(target.cursor, g.start + g.quarters)
		}

		// 连音里的休止：只在「该位置确实没有音」时才补。
		// 若那个位置已有音（另一个 voice 的音），补休止会把这个位置变成两个事件，
		// 时值凭空翻倍 —— 比丢一个音更糟。
		//
		// 归属层不能看 cursor（那时它已被本层最后一个音推到小节末尾），要用
		// 「时间上离得最近的同谱表层」来判。
		for(const {partIdx, staff, voice, ev} of pendingRests) {
			if(occupied.has(posKeyOf(partIdx, staff, voice, ev.start))) continue
			let target: Layer | undefined
			let bestDist = Infinity
			for(const l of layers) {
				if(l.partIdx != partIdx || l.staff != staff || l.voice != voice) continue
				const events = l.measures[mi]
				if(events.length == 0) continue
				for(const other of events) {
					const dist = Math.abs(other.start - ev.start)
					if(dist < bestDist) {
						bestDist = dist
						target = l
					}
				}
			}
			if(!target) continue
			target.measures[mi].push(ev)
		}
	}
	// 输出的最后一道清理：整首只有一两个音的「幽灵行」。
	//
	// 源谱里偶尔留着一条几乎全是休止的声部（转谱残留），单独输出就是一行空白谱，
	// 看起来像导入坏了。判据取很保守的双条件 —— 音数 >= 5 **且** 有音的小节占比
	// >= 10% 才算真声部：正常的长休止段落（左手后进、间奏）总有一头满足，
	// 而 52 小节里只有 5 个音那种两边都不满足。
	//
	// 这是**唯一**还主动丢内容的地方，所以数量必须报出来。
	if(layers.length > 1) {
		const notesOf = (l: Layer) => l.measures.reduce((s, evs) =>
			s + evs.filter(e => e.midi !== null).length, 0)
		const spansOf = (l: Layer) => l.measures.filter(evs =>
			evs.some(e => e.midi !== null)).length
		const survivors = layers.filter(l =>
			notesOf(l) >= 5 && spansOf(l) >= measureCount * 0.1)
		if(survivors.length) {
			for(const l of layers) {
				if(!survivors.includes(l)) droppedNotes += notesOf(l)
			}
			layers.splice(0, layers.length, ...survivors)
		}
	}

	if(droppedNotes > 0) {
		warnings.push(`已省略 ${droppedNotes} 个音符（来自几乎全是休止的残留声部）`)
	}

	// 安全阀（见 LAYER_SAFETY_CAP 的注释）
	if(layers.length > LAYER_SAFETY_CAP) {
		const total = layers.length
		const notesOf = (l: Layer) => l.measures.reduce((s, evs) =>
			s + evs.filter(e => e.midi !== null).length, 0)
		const ranked = [...layers].sort((a, b) => notesOf(b) - notesOf(a))
		const keep = new Set(ranked.slice(0, LAYER_SAFETY_CAP))
		let capped = 0
		for(const l of layers) {
			if(!keep.has(l)) capped += notesOf(l)
		}
		layers.splice(0, layers.length, ...layers.filter(l => keep.has(l)))
		warnings.push(`源谱有 ${total} 个声部，超出 ${LAYER_SAFETY_CAP} 行的部分已省略（保留音符最多的 ${LAYER_SAFETY_CAP} 条，共 ${capped} 个音）`)
	}
	return layers
}

export function musicXmlToNmn(xmlText: string): MusicXmlImportResult {
	const warnings: string[] = []
	const doc = new DOMParser().parseFromString(xmlText, 'application/xml')
	if(doc.querySelector('parsererror')) {
		throw new Error('XML 解析失败：文件不是合法的 MusicXML')
	}

	const title = doc.querySelector('work > work-title')?.textContent?.trim() || ''

	const partEls = [...doc.querySelectorAll('part')]
	if(partEls.length == 0) {
		throw new Error('未找到含有音符的声部')
	}

	const attrs: ScoreAttrs = {fifths: 0, beats: 4, beatType: 4, qpm: 90}
	const parts: ParsedPart[] = []
	for(const partEl of partEls) {
		const parsed = parsePart(partEl, attrs, warnings)
		if(parsed.raw.length > 0) parts.push(parsed)
	}
	const measureCount = parts.reduce((mx, p) => Math.max(mx, p.raw.length), 0)
	if(measureCount == 0 || !parts.some(p => p.hasPitch)) {
		throw new Error('未解析到任何音符')
	}

	const layers = buildLayers(parts, measureCount, warnings)
	if(layers.length == 0) {
		throw new Error('未解析到任何音符')
	}

	// 小节长度：取所有声部在该小节的**实际终止时间**，并与拍号比对。
	// 首小节短于拍号视为弱起，保留原长；其余小节补到拍号长度（谱面常省略尾部休止）。
	const tsQuarters = attrs.beats * 4 / attrs.beatType
	const measureLens = Array.from({length: measureCount}, (_, mi) => {
		let end = 0
		// 从**原始**事件统计（含休止符）：分层时休止被跳过了，用 layers 算会漏。
		for(const part of parts) {
			const m = part.raw[mi]
			if(!m) continue
			for(const evs of m.values()) {
				for(const ev of evs) end = Math.max(end, ev.start + ev.quarters)
			}
		}
		if(end <= EPS) return tsQuarters
		if(mi == 0 && end < tsQuarters - EPS) return end
		return Math.max(end, tsQuarters)
	})

	// 生成 NMN
	//
	// 调号基准必须归一化到 [-6, 5]：引擎里 `1=G` 的实际音高是 60 + base.value，
	// 而 G 的 base.value 是 **-5**（不是 7）、A 是 -3、F 是 5 —— 也就是主音不总落在
	// C4..B4 这一圈里，G 大调的主音其实是 G3。导出侧按这个口径算 MIDI，导入侧若按
	// 0..11 理解，G/A/F# 调的整首会低一个八度（实测往返一次掉 12，两次掉 24）。
	const baseSemis = (((fifthsToSemis[attrs.fifths] ?? 0) + 6) % 12 + 12) % 12 - 6
	const tonic = tonicNames[((baseSemis % 12) + 12) % 12]

	type NoteToken = {token: string, lyrics?: (string | undefined)[]}
	type LayerTokens = {
		/** 每小节的 token 列表 */
		measures: NoteToken[][]
		/** 该层歌词的最大段数 */
		maxVerse: number
		/** 源谱里的归属，决定行序（见下面的 order 排序） */
		partIdx: number
		staff: number
		voice: number
	}

	// 临时记号在本引擎里是**小节内按音级继承**的（`#2 2 2` 三个都是升 A，
	// 跨小节清零）。导入侧写的是绝对拼写，所以必须自己维护这个状态：
	// 否则 `#2` 后面那个真正的还原 A 会被读成升 A —— 差一个半音，还看不出错。
	/** 音高 → 「级数 + 变化音 + 八度」的字符（不含时值写法，连音分组要单独拼时值） */
	const pitchDigit = (ev: Ev, accState: Map<number, number>): string => {
		const midi = ev.midi ?? 60
		// 音高 → 级数 + 变化音 + 八度
		//
		// 优先级：本位音（a=0）优先，其次变化记号最少。循环顺序就是优先级顺序，
		// 命中即退出 —— 千万别用「(degree==1 && acc==0) 表示尚未命中」这种哨兵：
		// (1, 0) 本身就是合法的命中结果，哨兵恒为真，后面每个命中都会把它覆盖掉。
		const rel = midi - 60 - baseSemis
		const relMod = ((Math.round(rel) % 12) + 12) % 12
		let degree = 1
		let acc = 0
		outer: for(const a of [0, 1, -1, 2, -2]) {
			for(let d = 1; d <= 7; d++) {
				if(degreeSemis[d - 1] + a == relMod) {
					degree = d
					acc = a
					break outer
				}
			}
		}
		const octave = Math.round((rel - relMod) / 12)
		let digit = '' + degree
		const accBefore = accState.get(degree) ?? 0
		accState.set(degree, acc)
		if(acc > 0) digit = '#'.repeat(acc) + digit
		else if(acc < 0) digit = 'b'.repeat(-acc) + digit
		else if(accBefore != 0) digit = '=' + digit
		if(octave > 0) digit += 'e'.repeat(Math.min(octave, 3))
		if(octave < 0) digit += 'd'.repeat(Math.min(-octave, 3))
		return digit
	}

	/**
	 * 一个「位置」= 同一层里同一时刻的一簇音。
	 * 1 个音 = 单音；多个音 = 柱式和弦，输出成 `[1d,3d,5d]` 一个 token。
	 */
	type Pos = {
		start: number
		quarters: number
		/** 音高升序 —— 方括号里第一个音画在最下面，正好是低音 */
		evs: Ev[]
		tuplet?: {actual: number, normal: number}
	}

	/** 起点量化到 1/96 拍，避免浮点误差把同一个和弦拆成两个位置 */
	const posKey = (start: number) => Math.round(start * 96)

	/** 位置 → 音名字符串。和弦写成 `[...]`，休止写 `0`。 */
	const digitOf = (pos: Pos, accState: Map<number, number>): string => {
		const notes = pos.evs.filter(e => e.midi !== null)
		if(notes.length == 0) return '0'
		if(notes.length == 1) return pitchDigit(notes[0], accState)
		return '[' + notes.map(e => pitchDigit(e, accState)).join(',') + ']'
	}

	const toTokens = (pos: Pos, accState: Map<number, number>): NoteToken[] => {
		const notes = pos.evs.filter(e => e.midi !== null)
		if(notes.length == 0) {
			return restTokens(pos.quarters, warnings).map(token => ({token}))
		}
		// 时值后缀直接拼在 `]` 后面：`[135]` 拍、`[135].` 附点、`([135])` 八分。
		const tok = durationToken(digitOf(pos, accState), pos.quarters, warnings)
		return tok ? [{token: tok}] : []
	}

	/**
	 * 连音组 → token 列表；表达不了时返回 null，调用方退回普通时值。
	 *
	 * NMN 的写法是 `T[x,y]( ... )`，组内每个音的时值乘 x/y。关键是**T 后面那层
	 * 括号本身算一层减时线**，所以：
	 *
	 *   T[2,3](1 2 3)     组内名义是八分（1/2 拍） → 实际 1/3 拍
	 *   T[2,3]((1 2 3))   组内名义是十六分（1/4）  → 实际 1/6 拍
	 *   T[2,3](1. 2 3)    组内名义附点八分（3/4）  → 实际 1/2 拍
	 *
	 * 反推：给定实际时值 q 与比例 r = normal/actual，名义时值 = q/r，必须正好是
	 * 1/2^k 或附点 3/2^(k+1)，且 k ≥ 1（T 的括号已经吃掉一层）。
	 *
	 * 组内各个音的层数可以不同 —— 三连音里混着八分与十六分是常态，所以逐个算。
	 * x 取「组内音数 × r」：完整组时恰好等于 normal；分层把连音拆散时会算出
	 * 非整数，那种情况直接回退，不硬凑（硬凑会让时值差一截）。
	 *
	 * 组内元素是**位置**而不是单音：三连音里的一个和弦算一个位置，音名由
	 * `digitOf` 拼成 `[135]`，时值逻辑完全不变。
	 */
	const emitTuplet = (
		spec: {actual: number, normal: number},
		items: Pos[],
		accState: Map<number, number>,
	): NoteToken[] | null => {
		const count = items.length
		type LevelInfo = {level: number, dotted: boolean}

		/** 给定缩放比例 r，看组内每个音能不能写成「档位名义时值 × r」 */
		const pickLevels = (r: number): LevelInfo[] | null => {
			const out: LevelInfo[] = []
			for(const it of items) {
				const nominal = it.quarters / r
				let level = -1
				let dotted = false
				let bestDiff = Infinity
				for(let k = 1; k <= 6; k++) {
					const base = 1 / Math.pow(2, k)
					const dPlain = Math.abs(nominal - base)
					const dDotted = Math.abs(nominal - base * 1.5)
					if(dPlain < bestDiff) {
						bestDiff = dPlain
						level = k
						dotted = false
					}
					if(dDotted < bestDiff) {
						bestDiff = dDotted
						level = k
						dotted = true
					}
				}
				if(level < 1 || bestDiff > Math.max(0.01, nominal * 0.02)) return null
				out.push({level, dotted})
			}
			return out
		}

		let ratio = spec.normal / spec.actual
		let levels = pickLevels(ratio)
		if(!levels) {
			// 有一种常见形态写不出来：**四分音符名义**的连音（比如 3 个音占 2 拍，
			// 每个 2/3 拍）。T 后面那层括号本身就算一层减时线，所以组内最长的名义
			// 只能是八分。把比例整体乘 2（每个音的名义时值随之减半）就等价了：
			//   3 个音各 2/3 拍 = `T[2,3](1 2 3)` 写不出 → `T[4,3](1 2 3)`
			// 两者总时值都是 2 拍、谱面都显示「3」，只是 x/y 的取值不同。
			ratio = ratio * 2
			levels = pickLevels(ratio)
		}
		if(!levels) return null
		// 前缀里的分母 y 是**槽数**，等于源文件声明的 actual —— 不是音符个数。
		// 一个音可以横跨多个槽（三连音里「休止 + 占两槽的长音」），这时音数 < 槽数，
		// 按音数算会让整个组回退成普通时值。分子 x = 槽数 × 比例，正好还原成 normal。
		const slots = Math.max(spec.actual, count)
		const field = slots * ratio

		// ⚠️ digitOf 会写 accState（临时记号按音级小节内继承），所以必须**在确定
		// 走这条路之后**才调 —— 上面 return null 的分支一次都没调过，否则回退到
		// 普通时值时会重复记一次变化音状态。
		const bodies: string[] = []
		items.forEach((it, i) => {
			const {level, dotted} = levels![i]
			const digit = digitOf(it, accState)
			// 组内相对层数 = 名义层数 - 1（最外层那层由 T 的括号提供）
			const rel = level - 1
			bodies.push('('.repeat(rel) + digit + (dotted ? '.' : '') + ')'.repeat(rel))
		})

		// 前缀挂在第一个音、收尾括号挂在最后一个音上 —— 这样每个音仍是独立 token，
		// 歌词才能逐音对位（`Lc:` 是按音符顺序对齐的）。
		// 标准三连音写成惯用的裸 `T(`：`T[2,3](1 2 3)` 与 `T(1 2 3)` 等价，
		// 但后者是谱库里最常见的写法，导入结果看着才自然。
		const head = slots == 3 && Math.round(field) == 2
			? 'T('
			: `T[${Math.round(field)},${slots}](`
		bodies[0] = head + bodies[0]
		bodies[bodies.length - 1] += ')'
		return bodies.map(token => ({token}))
	}

	/** 一个位置里的歌词（和弦只有主音带词，取第一个带词的音） */
	const lyricOf = (pos: Pos): (string | undefined)[] | undefined => {
		for(const ev of pos.evs) {
			if(ev.lyrics.some(t => t)) return [...ev.lyrics]
		}
		return undefined
	}

	const layerTokens: LayerTokens[] = layers.map(layer => {
		const measures: NoteToken[][] = []
		let maxVerse = 0
		for(let mi = 0; mi < measureCount; mi++) {
			// 临时记号状态按小节清零（与引擎语义一致）
			const accState = new Map<number, number>()
			const notes: NoteToken[] = []
			let cursor = 0
			/** 「写出来」的时值累计，最后和小节应有长度核对 */
			let written = 0

			// 1) 把本小节的音按起点聚成「位置」。同起点 = 柱式和弦，必须拼进一个
			//    方括号 —— 这是「按原样保留」最关键的一步，旧版在这里只留根音。
			const byStart = new Map<number, Ev[]>()
			for(const ev of layer.measures[mi]) {
				const k = posKey(ev.start)
				let list = byStart.get(k)
				if(!list) byStart.set(k, list = [])
				list.push(ev)
			}
			const positions: Pos[] = [...byStart.entries()]
				.sort((a, b) => a[0] - b[0])
				.map(([, evs]) => ({
					start: evs[0].start,
					quarters: Math.max(...evs.map(e => e.quarters)),
					evs: [...evs].sort((a, b) => (a.midi ?? 0) - (b.midi ?? 0)),
					tuplet: evs[0].tuplet,
				}))

			// 2) 分组：连音组按「同比例 + 首尾相接 + 不超过 actual 个音」聚拢；
			//    非连音位置也聚成一组（tie 合并必须在组内做，不能跨连音边界）。
			//
			//    数量上限那条不能省：两个三连音紧挨着时，不加限制会被并成一个六音
			//    组，写成 `T[4,6]` —— 时值碰巧还对，但谱面会显示「6」而不是「3」。
			type Group = {tuplet?: {actual: number, normal: number}, items: Pos[]}
			const groups: Group[] = []
			for(const pos of positions) {
				const prevGroup = groups[groups.length - 1]
				const prevLast = prevGroup?.items[prevGroup.items.length - 1]
				const adjacent = prevLast !== undefined
					&& prevLast.start + prevLast.quarters >= pos.start - 0.01
				const bothPlain = prevGroup !== undefined && !pos.tuplet && !prevGroup.tuplet
				const sameTuplet = prevGroup?.tuplet !== undefined && pos.tuplet !== undefined
					&& pos.tuplet.actual == prevGroup.tuplet.actual
					&& pos.tuplet.normal == prevGroup.tuplet.normal
					&& prevGroup.items.length < prevGroup.tuplet.actual
				if(adjacent && (bothPlain || sameTuplet)) prevGroup.items.push(pos)
				else groups.push({tuplet: pos.tuplet, items: [pos]})
			}

			// 3) 逐组输出：空隙补休止，连音组走 T[x,y]，其余按普通时值
			for(const group of groups) {
				const first = group.items[0]
				if(first.start > cursor + EPS) {
					for(const tok of restTokens(first.start - cursor, warnings)) notes.push({token: tok})
					written += first.start - cursor
				}
				// 连音组：表达不了（时值不是二分时值的整数分之一，或分层把组拆散了）
				// 就退回普通时值 —— 宁可偏长，也不要写出一条引擎读不懂的谱。
				const tupletTokens = group.tuplet ? emitTuplet(group.tuplet, group.items, accState) : null
				if(group.tuplet && !tupletTokens) {
					const msg = `第 ${mi + 1} 小节有连音无法用 T[x,y] 表示（时值不是二分时值的整数分之一），已按普通时值近似`
					if(!warnings.includes(msg)) warnings.push(msg)
				}
				if(tupletTokens) {
					group.items.forEach((pos, i) => {
						const lyric = lyricOf(pos)
						if(lyric) tupletTokens[i].lyrics = lyric
					})
					for(const token of tupletTokens) notes.push(token)
					for(const pos of group.items) {
						const lyric = lyricOf(pos)
						if(lyric) maxVerse = Math.max(maxVerse, lyric.length)
						written += pos.quarters
					}
				} else {
					// 普通路径：先把 tie 链拼成一个长音（在 token 层面拼要回改成型的
					// 字符串，既易错又丢音高信息），再逐音转 token
					const merged: Pos[] = []
					for(const pos of group.items) {
						const last = merged[merged.length - 1]
						// 整簇音都标了 tie-stop、且音高与上一个位置逐个对齐 → 同一条延音。
						// 单音和和弦走同一套判据（和弦的延音是整簇一起延续）。
						const continues = last !== undefined
							&& pos.evs.every(e => e.tieStop)
							&& last.evs.length == pos.evs.length
							&& last.evs.every((le, i) => le.midi !== null && le.midi === pos.evs[i].midi)
							&& Math.abs(last.start + last.quarters - pos.start) < 0.01
						if(continues) {
							last.quarters += pos.quarters
							// 链头没有歌词而后续音符带了（少见），补到链头
							if(!lyricOf(last) && lyricOf(pos)) {
								last.evs = pos.evs.map(e => ({...e, lyrics: [...e.lyrics]}))
							}
							continue
						}
						merged.push({...pos, evs: pos.evs.map(e => ({...e, lyrics: [...e.lyrics]}))})
					}
					for(const pos of merged) {
						const toks = toTokens(pos, accState)
						if(toks.length == 0) continue
						notes.push(...toks)
						written += snapDuration(pos.quarters)
						const lyric = lyricOf(pos)
						if(lyric) {
							maxVerse = Math.max(maxVerse, lyric.length)
							toks[toks.length - 1].lyrics = lyric
						}
					}
				}
				const last = group.items[group.items.length - 1]
				cursor = Math.max(cursor, last.start + last.quarters)
			}
			if(measureLens[mi] > cursor + EPS) {
				for(const tok of restTokens(measureLens[mi] - cursor, warnings)) notes.push({token: tok})
				written += measureLens[mi] - cursor
			}

			// 兜底核对：写出来的时值 == 小节应有长度。连音已经精确，正常不会触发；
			// 真触发说明还有没覆盖的时值形态，必须显式提示（不能只剩「小节线对不齐」）。
			if(Math.abs(written - measureLens[mi]) > 0.02 && !warnings.includes(TUPLET_WARNING)) {
				warnings.push(TUPLET_WARNING)
			}
			measures.push(notes)
		}
		return {
			measures,
			maxVerse,
			partIdx: layer.partIdx,
			staff: layer.staff,
			voice: layer.voice,
		}
	})
	/**
	 * 行序：**按源谱的书写顺序**（part → staff → voice），有歌词的那行排最前。
	 *
	 * 为什么不再按「音符数 / 平均音高」排：那两个判据是在和弦被削薄的年代想的 ——
	 * 一条 3 音和弦的伴奏线只算 1 个音，所以音数多的必定是旋律。现在和弦按原样
	 * 保留，一条左手柱式伴奏的音数会反超旋律（5 个三音和弦 = 15 个音 vs 旋律
	 * 12 个），按音数排会把伴奏顶到第一行、旋律掉到 `N["伴奏"]:`，完全颠倒。
	 *
	 * 按源谱顺序排则是「所见即所得」：MusicXML 里第一谱表就是第一行，这与用户在
	 * MuseScore 里看到的一致。staff 1 通常是右手/旋律，正好也是我们想要的。
	 */
	const order = layerTokens.map((lt, i) => ({lt, i, idx: i}))
	order.sort((a, b) => {
		const la = a.lt.maxVerse > 0 ? 1 : 0
		const lb = b.lt.maxVerse > 0 ? 1 : 0
		return lb - la
			|| a.lt.partIdx - b.lt.partIdx
			|| a.lt.staff - b.lt.staff
			|| a.lt.voice - b.lt.voice
			|| a.idx - b.idx
	})

	// 每行几个小节：按**平均密度**估宽来定（各行小节数统一：混排会让小节线对不齐）。
	// ⚠️ 占位要按**渲染段数**算，不能数音符个数：一个 token 里可能带延长线
	//   （`6d - - -` 是 1 个 token，却占 4 个位置），全音符小节会被严重低估。
	const TOKEN_BUDGET = 22
	const widthOf = (notes: NoteToken[]) =>
		notes.reduce((sum, n) => sum + n.token.split(/\s+/).filter(Boolean).length, 0) + 1
	const measureWidth = (mi: number) =>
		order.reduce((mx, o) => Math.max(mx, widthOf(o.lt.measures[mi])), 0)
	const totalWidth = Array.from({length: measureCount}, (_, mi) => measureWidth(mi))
		.reduce((a, b) => a + b, 0)
	const avgWidth = totalWidth / Math.max(measureCount, 1)
	const perLine = Math.max(1, Math.min(4, Math.round(TOKEN_BUDGET / Math.max(avgWidth, 1))))

	const lines: string[] = []
	lines.push('Dt: ' + (title || '导入的曲谱'))
	lines.push(`P: 1=${tonic} ${attrs.beats}/${attrs.beatType} qpm=${Math.round(attrs.qpm)}`)
	lines.push('===')
	// 提示**不写进源码**。`T:` 是篇章级特殊文本行，篇章里只要有音乐片段就不能带它
	// （`special_children` 错误）；以前是把提示塞进结尾多写一个 `===` 造出来的空篇章，
	// 代价是多一个 empty_tree 警告 —— 两种都是往用户曲谱里塞垃圾。
	// 提示改由调用方展示（网页版弹窗 / 桌面版 alert），源码保持干净。
	for(let i = 0; i < measureCount; i += perLine) {
		const chunk = Array.from({length: Math.min(perLine, measureCount - i)}, (_, k) => i + k)
		const lastChunk = i + perLine >= measureCount
		order.forEach(({lt}, lineIdx) => {
			// 第一行用普通 N:，其余声部带名字（`N["伴奏"]:`），与自动伴奏写回一致
			const label = lineIdx == 0 ? '' : (lineIdx == 1 ? '伴奏' : `声部${lineIdx + 1}`)
			const head = label ? `N["${label}"]: ` : 'N: '
			lines.push(head + chunk
				.map(mi => lt.measures[mi].map(n => n.token).join(' '))
				.join(' | ')
				+ (lastChunk ? ' |||' : ' |'))
			// 歌词只跟第一行（旋律）
			if(lineIdx != 0) return
			for(let v = 0; v < lt.maxVerse; v++) {
				const verseTokens = chunk.flatMap(mi =>
					lt.measures[mi].map(n => n.lyrics?.[v]).filter(t => t != undefined && t !== '')
				)
				if(verseTokens.length > 0) {
					lines.push('Lc: ' + verseTokens.join(' '))
				}
			}
		})
		if(!lastChunk) lines.push('---')
	}
	// ⚠️ 不要补结尾的 `===`。`===` 是**开始**一个篇章的标记，不是结束标记；
	// 末尾再写一个等于凭空多出一个空篇章，解析器会对它报 empty_tree 警告
	// （编辑器末行挂个黄叹号，看着像曲谱有错）。
	return {
		title: title || '导入的曲谱',
		content: lines.join('\n') + '\n',
		warnings,
	}
}
